
export function appDomain(): string | null {
  const labels = window.location.hostname.split(".");
  if (labels.length < 2) return null;
  if (labels.length === 2) return labels.join(".");
  return labels.slice(1).join(".");
}

/** True when this build can address workspaces by subdomain at all. */
export function subdomainRouting(): boolean {
  return appDomain() !== null;
}

/** The origin of one workspace. Null slug means the apex — your own. */
export function workspaceUrl(slug: string | null): string | null {
  const domain = appDomain();
  if (!domain) return null;

  const { protocol, port } = window.location;
  const host = slug === null ? domain : `${slug}.${domain}`;

  return `${protocol}//${host}${port ? `:${port}` : ""}/`;
}

/** The workspace slug in the address bar, or null on the apex. */
export function currentSlug(): string | null {
  const domain = appDomain();
  const { hostname } = window.location;

  if (!domain || hostname === domain) return null;
  if (!hostname.endsWith(`.${domain}`)) return null;

  const label = hostname.slice(0, -(domain.length + 1));
  return label.includes(".") ? null : label;
}

/**
 * Where signing in happens — always the apex, never a workspace subdomain.
 *
 * ‼️ GOOGLE ALLOWS NO WILDCARD ORIGIN. `google.accounts.id` checks the page's
 *    origin against a list registered on the client id, so a button on
 *    `kd.<domain>` would be refused and one origin per school is not a list
 *    anybody can maintain. One host signs in; the cookie spans the rest.
 */
export function authOrigin(): string | null {
  return workspaceUrl(null);
}

/**
 * A `?next=` value, if it points into our own domain.
 *
 * ‼️ VALIDATED, NOT TRUSTED. `next` arrives in a URL anyone can craft, and a
 *    sign-in page that redirects wherever it is told is an open redirect —
 *    the classic way a phishing link borrows a real login screen.
 */
export function returnUrl(next: string | null): string | null {
  if (!next) return null;

  const domain = appDomain();
  if (!domain) return null;

  try {
    const url = new URL(next, window.location.origin);
    if (url.protocol !== window.location.protocol) return null;
    if (url.hostname !== domain && !url.hostname.endsWith(`.${domain}`)) {
      return null;
    }
    return url.toString();
  } catch {
    return null;
  }
}

export function isOurOrigin(origin: string): boolean {
  const domain = appDomain();
  if (!domain) return origin === window.location.origin;

  try {
    const url = new URL(origin);
    if (url.protocol !== window.location.protocol) return false;
    return url.hostname === domain || url.hostname.endsWith(`.${domain}`);
  } catch {
    return false;
  }
}
